import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer, RefusalCard } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { Figure, PageHeader, bacenCasesHref, useCodeLabel, useFormats } from '@/pages/bacen/shared';
import { fetchBacenCollection } from '@/services/bacen';
import type { AnswerOut, BacenCollectionTable } from '@/services/types';

/**
 * Where collection stands: the fifteen Situacao values folded into families,
 * by decision year and by fine band. Recovery in money and lateness are
 * refused -- the ledger carries no paid amount and no payment date -- and
 * the refusals carry the owed count they were measured against.
 */
export function BacenCollectionPage() {
  const { t } = useTranslation(['bacen', 'common']);
  const { number, currency, percent } = useFormats();
  const label = useCodeLabel();
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['bacen', 'collection'],
    queryFn: fetchBacenCollection,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={6} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6">
      <PageHeader page="bacen:collection" exportPath="/api/v1/bacen/export/collection" />

      <AsOfStrip asOf={data.as_of} />

      <section className="rounded-lg border bg-card p-4" data-status-mix>
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('bacen:collection.mix.title')}
        </h2>
        <Answer answer={data.status_mix} help={t('bacen:collection.mix.help')}>
          {(mix) => (
            <div className="space-y-3">
              <dl className="grid grid-cols-3 gap-3">
                <Figure
                  label={t('bacen:collection.mix.closed')}
                  value={number.format(mix.closed)}
                  href={bacenCasesHref({ status_family: 'PAID' })}
                />
                <Figure
                  label={t('bacen:collection.mix.owed')}
                  value={`${number.format(mix.owed)} (${percent.format(mix.owed_share)})`}
                  href={bacenCasesHref({ status_family: 'IN_COLLECTION' })}
                  testId="owed"
                />
                <Figure
                  label={t('bacen:collection.mix.owedTotal')}
                  value={
                    mix.fines_owed_total === null ? '—' : currency.format(mix.fines_owed_total)
                  }
                />
              </dl>
              <ul className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                {mix.families.map((f) => (
                  <li
                    key={f.code}
                    className="rounded-md border bg-muted/30 p-2"
                    data-family={f.code}
                  >
                    <div className="text-xs text-muted-foreground">
                      {label('statusFamily', f.code)}
                    </div>
                    <div className="font-semibold tabular-nums text-foreground">
                      <Link
                        to={bacenCasesHref({ status_family: f.code })}
                        className="text-primary underline-offset-2 hover:underline"
                      >
                        {number.format(f.count)}
                      </Link>
                      <span className="ml-1 text-xs font-normal text-muted-foreground">
                        ({percent.format(f.share)})
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Answer>
        <div className="mt-3">
          <FieldGuide i18nKey="bacen:collection.guide.mix" />
        </div>
      </section>

      <CollectionTablePanel answer={data.by_year} />
      <CollectionTablePanel answer={data.by_band} />

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-foreground">
          {t('bacen:collection.refusals.title')}
        </h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <RefusalCard title={t('bacen:collection.refusals.recovery')} answer={data.recovery} />
          <RefusalCard title={t('bacen:collection.refusals.lateness')} answer={data.lateness} />
        </div>
      </section>
    </div>
  );
}

function CollectionTablePanel({ answer }: { answer: AnswerOut<BacenCollectionTable> }) {
  const { t } = useTranslation(['bacen']);
  const { number, percent } = useFormats();
  const kind = answer.value?.kind ?? 'year';
  return (
    <section className="rounded-lg border bg-card p-4" data-collection-table={kind}>
      <h2 className="mb-3 text-sm font-semibold text-foreground">
        {t(`bacen:collection.groups.${kind}.title`)}
      </h2>
      <Answer answer={answer} help={t(`bacen:collection.groups.${kind}.help`)}>
        {(table) => (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">
                    {t(`bacen:collection.groups.${kind}.group`)}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.rows')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.paid')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.served')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.owed')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.noPenalty')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('bacen:collection.groups.other')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row) => (
                  <tr key={row.group} className="border-t" data-group={row.group}>
                    <td className="px-3 py-2 text-foreground">
                      {kind === 'year' ? (
                        <Link
                          to={bacenCasesHref({ year: Number(row.group) })}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {row.group}
                        </Link>
                      ) : (
                        // "2: 5k-25k": the digit prefix only orders the rows.
                        row.group.replace(/^\d+:\s*/, '')
                      )}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.rows)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {number.format(row.paid)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({percent.format(row.paid_share)})
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.served)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {number.format(row.owed)}
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({percent.format(row.owed_share)})
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.no_penalty)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(row.other)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Answer>
    </section>
  );
}
